import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatAuthError, verifySessionPersisted } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", {
        replace: true,
        state: { error: "Google sign-in didn't return a session. Please try again." },
      });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(
          "/api/auth/session",
          { session_id: sessionId },
          { headers: { "X-Session-ID": sessionId } }
        );
        // Confirm the browser actually kept the session cookie. Safari ITP /
        // strict tracking-prevention mode will silently drop it, so we'd
        // navigate to /dashboard and immediately get bounced back.
        const persisted = await verifySessionPersisted();
        if (!persisted) {
          navigate("/login", {
            replace: true,
            state: { cookieBlocked: true },
          });
          return;
        }
        setUser(data);
        // strip hash and redirect
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (err) {
        // Surface the actual reason instead of a generic "Google sign-in failed".
        const reason = formatAuthError(
          err,
          "Google sign-in failed. Please try again."
        );
        navigate("/login", { replace: true, state: { error: reason } });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="label-mono" data-testid="auth-callback-loading">CONNECTING GOOGLE SESSION…</div>
    </div>
  );
}
